import { DEFAULT_WEEK_RANGE, fetchScoreboard, fetchWeekScoreboard } from './espn';
import { enrichGamesWithSummaries } from './espnIntel';
import { createLocalStore, listSeasonWeekKeys, loadWeekState } from './localStore';
import {
  createSupabaseStore,
  getSupabaseClient,
  listPickWeekKeys,
  loadSeasonAdjustments as fetchSeasonAdjustments,
  loadSheetsForWeek,
} from './supabaseStore';
import { findTiebreakerGame } from './scoring';
import type { PlayerId } from './roster';
import type { Game, PickStore, WeekState } from './types';
import { parseWeekNumber, parseWeekYear } from './weeks';

export function mergeSeasonWeeks(current: WeekState, others: WeekState[]): WeekState[] {
  const byKey = new Map<string, WeekState>();
  for (const week of others) byKey.set(week.weekKey, week);
  byKey.set(current.weekKey, current);
  return [...byKey.values()].sort((a, b) =>
    a.weekKey.localeCompare(b.weekKey, undefined, { numeric: true }),
  );
}

export async function loadSeasonAdjustments(): Promise<Partial<Record<PlayerId, number>>> {
  const client = getSupabaseClient();
  return client ? fetchSeasonAdjustments(client) : {};
}

export async function loadSeasonWeeks(store: PickStore): Promise<WeekState[]> {
  const current = store.getWeek();
  const client = getSupabaseClient();
  if (!client) {
    const others = listSeasonWeekKeys()
      .map(loadWeekState)
      .filter((week): week is WeekState => week !== null);
    return mergeSeasonWeeks(current, others);
  }

  const keys = new Set<string>([current.weekKey]);
  try {
    for (const key of await listPickWeekKeys(client)) keys.add(key);
  } catch {
    // fail open: still score the week on screen
  }

  const others: WeekState[] = [];
  for (const key of keys) {
    if (key === current.weekKey) continue;
    try {
      const board = await fetchWeekScoreboard(parseWeekNumber(key), parseWeekYear(key));
      const sheets = await loadSheetsForWeek(client, key);
      others.push({
        weekKey: key,
        dateRange: board.dateRange,
        games: board.games,
        sheets,
        tiebreakerGameId: findTiebreakerGame(board.games)?.id ?? null,
      });
    } catch {
      continue;
    }
  }
  return mergeSeasonWeeks(current, others);
}

export async function createStore(): Promise<{
  store: PickStore;
  dateRange: string;
  espnError: string | null;
}> {
  let dateRange = DEFAULT_WEEK_RANGE;
  let weekKey = '2026-W1';
  let games: Game[] = [];
  let espnError: string | null = null;

  try {
    const board = await fetchScoreboard();
    games = board.games;
    dateRange = board.dateRange;
    weekKey = board.weekKey;
  } catch {
    try {
      const board = await fetchScoreboard(DEFAULT_WEEK_RANGE);
      games = board.games;
      dateRange = board.dateRange;
      weekKey = board.weekKey;
    } catch (err) {
      espnError = err instanceof Error ? err.message : 'Failed to load games';
      games = [];
    }
  }

  const client = getSupabaseClient();
  const store = client
    ? createSupabaseStore(client, weekKey, dateRange, games)
    : createLocalStore(weekKey, dateRange, games);

  await store.init();
  if (games.length > 0) {
    await store.refreshGames(games);
  }

  return { store, dateRange, espnError };
}

export async function hydrateGameIntel(store: PickStore): Promise<void> {
  const enriched = await enrichGamesWithSummaries(store.getWeek().games);
  await store.refreshGames(enriched);
}

export async function retryEspn(store: PickStore, opts?: { summaries?: boolean }): Promise<string | null> {
  try {
    const week = store.getWeek();
    const board = await fetchWeekScoreboard(parseWeekNumber(week.weekKey), parseWeekYear(week.weekKey));
    await store.refreshGames(board.games);
    if (opts?.summaries) void hydrateGameIntel(store);
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'Failed to load games';
  }
}

export async function loadEspnWeek(store: PickStore, week: number, year: number): Promise<string | null> {
  try {
    const board = await fetchWeekScoreboard(week, year);
    await store.replaceWeek(board);
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'Failed to load games';
  }
}
